import { command, computed, state } from 'ccstate'
import { match } from 'path-to-regexp'
import { currentRoute$, generateRouterPath } from './routeRegistry'
import { sharedLocationState } from '../../state/sharedLocationState'
import { detach } from '../../../common/ccstatex/promise'
import { onDomEventFn } from '../../../common/ccstatex/onDomEvent'
import { resetSignal } from '../../../common/ccstatex/signals'
import { updatePage$ } from './currentPage'
import { rootSignal$ } from '../../../common/ccstatex/rootSignal'
import { logger } from '../../../common/infra/log'
import { afterNavigateReportToSentry } from '../sentryClient'

const L = logger('navigate')

const innerPageSignal$ = state<AbortSignal | undefined>(undefined)

export const pageSignal$ = computed((get) => {
  const signal = get(innerPageSignal$)
  if (!signal) {
    throw new Error('No page signal')
  }
  return signal
})

export const setPageSignal$ = command(({ set }, signal: AbortSignal) => {
  set(innerPageSignal$, signal)
})

export const pathParams$ = computed((get) => {
  const currentRoute = get(currentRoute$)
  if (!currentRoute) {
    return undefined
  }
  const matcher = match(currentRoute.path, { decode: decodeURIComponent })
  const currentPath = get(sharedLocationState.pathname$)
  const result = matcher(currentPath)
  return result ? result.params : undefined
})

const resetRouteSignal$ = resetSignal()

const loadRoute$ = command(async ({ get, set }, signal?: AbortSignal) => {
  const routeSignal = set(resetRouteSignal$, ...([signal].filter(Boolean) as AbortSignal[]))
  const currentRoute = get(currentRoute$)
  if (!currentRoute) {
    throw new Error('No route matches, pathname: ' + get(sharedLocationState.pathname$))
  }
  set(setPageSignal$, routeSignal)
  routeSignal.throwIfAborted()
  if (currentRoute.onBeforeNavigate) {
    const redirectTo = await get(currentRoute.onBeforeNavigate)
    if (redirectTo !== undefined) {
      await set(navigateInReact$, redirectTo)
      return
    }
  }
  routeSignal.throwIfAborted()
  const view = currentRoute.viewFactory()
  set(updatePage$, view)
  afterNavigateReportToSentry(currentRoute.path)
})

const navigateToDefaultWhenInvalid$ = command(({ get, set }) => {
  if (!get(currentRoute$)) {
    set(sharedLocationState.reloadPathname$, (x) => x + 1)
    window.history.pushState({}, '', '/')
  }
})

export const initRoutes$ = command(async ({ set }, signal: AbortSignal) => {
  set(navigateToDefaultWhenInvalid$)

  window.addEventListener(
    'popstate',
    onDomEventFn(async () => {
      set(sharedLocationState.reloadPathname$, (x) => x + 1)
      set(navigateToDefaultWhenInvalid$)
      await set(loadRoute$, signal)
    }),
    { signal },
  )

  await set(loadRoute$, signal)
})

interface NavigateOptions {
    searchParams?: URLSearchParams
}

export const navigate$ = command(async ({ set }, pathname: string, options: NavigateOptions, signal: AbortSignal) => {
  L.debug(L.fmt`navgiate to ${pathname}`)
  const searchParams = options.searchParams ? `?${options.searchParams.toString()}` : ''
  window.history.pushState({}, '', `${pathname}${searchParams}`)
  set(sharedLocationState.reloadPathname$, (x) => x + 1)
  await set(loadRoute$, signal)
})

export const navigateInReact$ = command(
  (
    { set, get },
    pathname: Parameters<typeof generateRouterPath>[0],
    options?: {
            pathParams?: Parameters<typeof generateRouterPath>[1]
            searchParams?: URLSearchParams
        },
  ) => {
    const signal = get(rootSignal$)

    detach(
      set(navigate$, generateRouterPath(pathname, options?.pathParams), options ?? {}, signal),
      'DomCallback',
    )
  },
)
